import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchAllSchemes,
  fetchAllSchemesOnBoarding,
  postPatientScheme,
  updatePatientScheme,
} from "../apis/schemeService";
import { fetchPatientSchemes } from "../apis/patientService";
import {
  ISchemeForm,
  ISchemeResponse,
  ISchemeState,
} from "../interfaces/IScheme";

const initialState: ISchemeState = {
  isFetchingScheme: false,
  isPostScheme: false,
  isUpdatedScheme: false,
  isUpdatedSchemeSuccessfully: false,
  schemes: [],
  error: null,
  selectedScheme: null,
  patientSchemes: null,
  patientSchemesList: {},
  schemeOnBoarding: []
};

export const fetchAllSchemesMetaData = createAsyncThunk(
  "scheme/fetchAllSchemes",
  async ({ accessToken }: { accessToken: string }) => {
    const response = await fetchAllSchemes();
    return response;
  }
);

export const fetchAllSchemesOnBoard = createAsyncThunk(
  "scheme/fetchAllSchemesOnBoarding",
  async ({ accessToken, healthCareId }: { accessToken: string, healthCareId: number }) => {
    const response = await fetchAllSchemesOnBoarding(accessToken, healthCareId);
    return response;
  }
);

export const addPatientScheme = createAsyncThunk(
  "scheme/addPatientScheme",
  async ({
    formData,
    accessToken,
    patientId,
    schemeId,
  }: {
    formData: ISchemeForm;
    accessToken: string;
    patientId: number | undefined;
    schemeId: number;
  }) => {
    const response = await postPatientScheme(formData, patientId, schemeId);
    return response;
  }
);

export const getPatientSchemes = createAsyncThunk(
  "scheme/getPatientSchemes",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchPatientSchemes(id);
    return response;
  }
);

export const updatePatchPatientScheme = createAsyncThunk(
  "scheme/updatePatchPatientScheme",
  async ({
    formData,
    accessToken,
    patientId,
    schemeId,
    id,
  }: {
    formData: ISchemeForm;
    accessToken: string;
    patientId: number;
    schemeId: number;
    id: number;
  }) => {
    const response = await updatePatientScheme(
      formData,
      patientId,
      schemeId,
      id
    );
    return response;
  }
);

const schemeSlice = createSlice({
  name: "scheme",
  initialState: initialState,
  reducers: {
    selectedScheme: (state, action: PayloadAction<string | null>) => {
      state.selectedScheme = action.payload;
    },
    updatePatientSchemeList: (state, action) => {
      if (action.payload?.id) {
        if (state.patientSchemesList[action.payload?.patientId]) {
          state.patientSchemesList[action.payload?.patientId] = [
            ...state.patientSchemesList[action.payload?.patientId],
            action.payload,
          ];
        }
      }
    },
    clearSelectedScheme: (state) => {
      state.selectedScheme = null;
    },
  },
  extraReducers: (builders) => {
    builders
      .addCase(fetchAllSchemesMetaData.pending, (state) => {
        state.isFetchingScheme = true;
        state.error = null;
      })
      .addCase(
        fetchAllSchemesMetaData.fulfilled,
        (state, action: PayloadAction<ISchemeResponse>) => {
          state.isFetchingScheme = false;
          if (action.payload.schemes) {
            state.schemes = action.payload.schemes;
          }
          state.error =
            action.payload.errorMessage || "Failed to fetch Schemes";
        }
      )
      .addCase(fetchAllSchemesMetaData.rejected, (state, action) => {
        state.isFetchingScheme = false;
        state.error = action.error.message || "Failed to Fetch All Schemes";
      })
      .addCase(addPatientScheme.pending, (state) => {
        state.isPostScheme = false;
        state.error = null;
      })
      .addCase(addPatientScheme.fulfilled, (state, action) => {
        state.isPostScheme = false;

        // state.patientSchemes = action.payload?.id ? action.payload : null;
        if (action.payload?.id) {
          if (state.patientSchemesList[action.payload?.patientId]) {
            state.patientSchemesList[action.payload?.patientId] = [
              ...state.patientSchemesList[action.payload?.patientId],
              action.payload,
            ];
          } else {
            state.patientSchemesList[action.payload?.patientId] = [
              action.payload,
            ];
          }
        }
      })
      .addCase(addPatientScheme.rejected, (state, action) => {
        state.isPostScheme = false;
        state.error = action.error.message || "Failed to post Schemes";
      })
      .addCase(getPatientSchemes.pending, (state) => {
        state.error = null;
      })
      .addCase(getPatientSchemes.fulfilled, (state, action) => {
        state.patientSchemesList[action.payload?.schemeNames[0]?.patientId] =
          action.payload.schemeNames || null;
      })
      .addCase(getPatientSchemes.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch patient schemes";
      })
      .addCase(updatePatchPatientScheme.pending, (state) => {
        state.isUpdatedScheme = true;
        state.isUpdatedSchemeSuccessfully = false;
        state.error = null;
      })
      .addCase(updatePatchPatientScheme.fulfilled, (state) => {
        state.isUpdatedScheme = false;
        state.isUpdatedSchemeSuccessfully = true;
        state.patientSchemesList = {};
        state.schemes = [];
      })
      .addCase(updatePatchPatientScheme.rejected, (state, action) => {
        state.isUpdatedScheme = false;
        state.isUpdatedSchemeSuccessfully = false;
        state.error = action.error.message || "Failed to Update patient scheme";
      })
      .addCase(fetchAllSchemesOnBoard.pending, (state) => {
        state.isFetchingScheme = true;
        state.error = null;
      })
      .addCase(fetchAllSchemesOnBoard.fulfilled, (state, action) => {
        state.isFetchingScheme = false;
        state.schemeOnBoarding = action.payload;
      })
      .addCase(fetchAllSchemesOnBoard.rejected, (state, action) => {
        state.isFetchingScheme = false;
        state.error =
          action.error.message || "Failed to Fetch All Schemes of onboarding";
      });
  },
});

export const { selectedScheme, updatePatientSchemeList, clearSelectedScheme } =
  schemeSlice.actions;
export default schemeSlice.reducer;
